import { useEffect, useState } from "react";

import { useToast } from "../App";
import { api } from "../lib/api";
import {
  CARD_TYPE_LABEL,
  confidenceTone,
  flagLabel,
  preview,
  sourceLabel,
} from "../lib/format";
import type { Card, CardType, MediaAsset, OcclusionShape } from "../lib/types";
import ClozeEditor from "./ClozeEditor";
import OcclusionEditor from "./OcclusionEditor";

interface Props {
  card: Card;
  media: Map<string, MediaAsset>;
  cardsById: Map<string, Card>;
  selected: boolean;
  highlighted: boolean;
  onToggleSelect: (id: string) => void;
  onChanged: (card: Card) => void;
  onRemoved: (id: string) => void;
}

export default function CardItem({
  card,
  media,
  cardsById,
  selected,
  highlighted,
  onToggleSelect,
  onChanged,
  onRemoved,
}: Props) {
  const { pushError, push } = useToast();
  const [editing, setEditing] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenPrompt, setRegenPrompt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [draft, setDraft] = useState({
    type: card.type as CardType,
    front: card.front,
    back: card.back,
    cloze_text: card.cloze_text,
    extra: card.extra,
    header: card.header,
    tags: (card.tags ?? []).join(" "),
    difficulty: card.difficulty,
    occlusion_shapes: card.occlusion_shapes ?? [],
  });

  useEffect(() => {
    setDraft({
      type: card.type,
      front: card.front,
      back: card.back,
      cloze_text: card.cloze_text,
      extra: card.extra,
      header: card.header,
      tags: (card.tags ?? []).join(" "),
      difficulty: card.difficulty,
      occlusion_shapes: card.occlusion_shapes ?? [],
    });
  }, [card]);

  const asset = card.media_id ? media.get(card.media_id) : undefined;
  const duplicateOf = card.duplicate_of_id ? cardsById.get(card.duplicate_of_id) : undefined;

  const setStatus = async (status: Card["status"]) => {
    setBusy(true);
    try {
      onChanged(await api.updateCard(card.id, { status }));
    } catch (error) {
      pushError(error);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const updated = await api.updateCard(card.id, {
        type: draft.type,
        front: draft.front,
        back: draft.back,
        cloze_text: draft.cloze_text,
        extra: draft.extra,
        header: draft.header,
        difficulty: draft.difficulty,
        tags: draft.tags.split(/\s+/).filter(Boolean),
        occlusion_shapes: draft.occlusion_shapes,
      });
      onChanged(updated);
      setEditing(false);
      const errors = updated.qc?.errors ?? [];
      if (errors.length) push(errors[0].message, "error");
    } catch (error) {
      pushError(error);
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (instruction: string) => {
    setRegenerating(true);
    setRegenPrompt(null);
    try {
      onChanged(await api.regenerateCard(card.id, instruction));
      push("Card rewritten from its source passage.", "good");
    } catch (error) {
      pushError(error);
    } finally {
      setRegenerating(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this card permanently?")) return;
    try {
      await api.deleteCard(card.id);
      onRemoved(card.id);
    } catch (error) {
      pushError(error);
    }
  };

  const tone = confidenceTone(card.confidence);
  const issues = [...new Set([...(card.flags ?? []), ...(card.qc?.issues ?? [])])];

  return (
    <article
      className={`review-card is-${card.status}${selected ? " is-selected" : ""}${
        highlighted ? " just-changed" : ""
      }`}
    >
      <div className="card-head">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(card.id)}
          aria-label="Select card"
        />
        <span className="chip chip-outline">{CARD_TYPE_LABEL[card.type] ?? card.type}</span>
        {card.status === "flagged" && <span className="chip chip-warn">Needs a look</span>}
        {card.status === "accepted" && <span className="chip chip-good">Accepted</span>}
        {card.status === "rejected" && <span className="chip">Rejected</span>}
        {card.status === "duplicate" && <span className="chip chip-bad">Duplicate</span>}
        {card.edited_by_user && <span className="chip">Edited</span>}

        <div className="grow" />

        <span className={`confidence confidence-${tone}`} title="How confident the AI is in this card">
          <span className="confidence-bar">
            <i style={{ width: `${Math.round(card.confidence * 100)}%` }} />
          </span>
          {Math.round(card.confidence * 100)}%
        </span>
        <span className="chip" title="Difficulty">
          D{card.difficulty}
        </span>
      </div>

      {editing ? (
        <div className="editor-grid">
          <label className="field" style={{ margin: 0 }}>
            <span className="label">Card type</span>
            <div className="segmented">
              {(["basic", "basic_reversed", "cloze"] as CardType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  aria-pressed={draft.type === type}
                  disabled={card.type === "image_occlusion"}
                  onClick={() => setDraft({ ...draft, type })}
                >
                  {CARD_TYPE_LABEL[type]}
                </button>
              ))}
            </div>
          </label>

          {draft.type === "cloze" && card.type !== "image_occlusion" && (
            <label className="field" style={{ margin: 0 }}>
              <span className="label">Cloze text</span>
              <ClozeEditor
                value={draft.cloze_text}
                onChange={(cloze_text) => setDraft({ ...draft, cloze_text })}
              />
            </label>
          )}

          {card.type === "image_occlusion" && asset && (
            <label className="field" style={{ margin: 0 }}>
              <span className="label">Occlusion regions</span>
              <OcclusionEditor
                imageUrl={api.mediaUrl(asset.id)}
                shapes={draft.occlusion_shapes as OcclusionShape[]}
                onChange={(occlusion_shapes) => setDraft({ ...draft, occlusion_shapes })}
              />
            </label>
          )}

          {card.type === "image_occlusion" && (
            <label className="field" style={{ margin: 0 }}>
              <span className="label">Header</span>
              <input
                type="text"
                value={draft.header}
                onChange={(e) => setDraft({ ...draft, header: e.target.value })}
              />
            </label>
          )}

          {draft.type !== "cloze" && card.type !== "image_occlusion" && (
            <>
              <label className="field" style={{ margin: 0 }}>
                <span className="label">Front</span>
                <textarea
                  value={draft.front}
                  onChange={(e) => setDraft({ ...draft, front: e.target.value })}
                  style={{ minHeight: 60 }}
                />
              </label>
              <label className="field" style={{ margin: 0 }}>
                <span className="label">Back</span>
                <textarea
                  value={draft.back}
                  onChange={(e) => setDraft({ ...draft, back: e.target.value })}
                  style={{ minHeight: 60 }}
                />
              </label>
            </>
          )}

          <label className="field" style={{ margin: 0 }}>
            <span className="label">Extra (optional)</span>
            <textarea
              value={draft.extra}
              onChange={(e) => setDraft({ ...draft, extra: e.target.value })}
              style={{ minHeight: 44 }}
            />
          </label>

          <div className="row row-wrap">
            <label className="field grow" style={{ margin: 0 }}>
              <span className="label">Tags (space separated)</span>
              <input
                type="text"
                value={draft.tags}
                onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
              />
            </label>
            <label className="field" style={{ margin: 0, width: 110 }}>
              <span className="label">Difficulty</span>
              <input
                type="number"
                min={1}
                max={5}
                value={draft.difficulty}
                onChange={(e) => setDraft({ ...draft, difficulty: Number(e.target.value) })}
              />
            </label>
          </div>

          <div className="row">
            <button className="btn-primary btn-sm" onClick={save} disabled={busy}>
              Save
            </button>
            <button className="btn-ghost btn-sm" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <div className="grow" />
            <button className="btn-ghost btn-sm btn-bad" onClick={remove}>
              Delete
            </button>
          </div>
        </div>
      ) : (
        <div className="card-body">
          {card.type === "cloze" ? (
            <div className="qa-line">
              <span className="qa-label">Cloze</span>
              <div
                className="qa-text cloze-render"
                dangerouslySetInnerHTML={{ __html: renderCloze(card.cloze_text) }}
              />
            </div>
          ) : card.type === "image_occlusion" ? (
            <>
              {card.header && (
                <div className="qa-line">
                  <span className="qa-label">Header</span>
                  <div className="qa-text">{card.header}</div>
                </div>
              )}
              {asset && (
                <div className="qa-line">
                  <span className="qa-label">Image</span>
                  <div className="qa-text">
                    <OcclusionPreview url={api.mediaUrl(asset.id)} shapes={card.occlusion_shapes} />
                    <div className="small faint" style={{ marginTop: 4 }}>
                      {card.occlusion_shapes.length} region
                      {card.occlusion_shapes.length === 1 ? "" : "s"} →{" "}
                      {card.occlusion_shapes.length} card
                      {card.occlusion_shapes.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="qa-line">
                <span className="qa-label">Front</span>
                <div className="qa-text" dangerouslySetInnerHTML={{ __html: card.front }} />
              </div>
              <div className="qa-line">
                <span className="qa-label">Back</span>
                <div className="qa-text" dangerouslySetInnerHTML={{ __html: card.back }} />
              </div>
            </>
          )}

          {card.extra && (
            <div className="qa-line">
              <span className="qa-label">Extra</span>
              <div className="qa-text muted small" dangerouslySetInnerHTML={{ __html: card.extra }} />
            </div>
          )}
        </div>
      )}

      {issues.length > 0 && (
        <div className="row row-wrap" style={{ marginTop: 10, gap: 5 }}>
          {issues.slice(0, 6).map((flag) => (
            <span key={flag} className="chip chip-warn">
              {flagLabel(flag)}
            </span>
          ))}
        </div>
      )}

      {card.qc?.suggested_fix && (
        <div className="small muted" style={{ marginTop: 8 }}>
          <strong>Suggested fix:</strong> {card.qc.suggested_fix}
        </div>
      )}

      {duplicateOf && (
        <div className="dup-warning">
          Duplicate of{" "}
          <em>{preview(duplicateOf)}</em>
          {card.duplicate_score != null && ` (${Math.round(card.duplicate_score * 100)}% similar)`}
        </div>
      )}

      <div className="card-foot">
        {(card.source_refs ?? []).slice(0, 2).map((ref, i) => (
          <button
            key={i}
            className="source-badge"
            onClick={() => setShowEvidence((v) => !v)}
            title="Show the passage this card came from"
          >
            📄 {sourceLabel(ref)}
          </button>
        ))}
        {!card.source_refs?.length && card.evidence && (
          <button className="source-badge" onClick={() => setShowEvidence((v) => !v)}>
            📄 Source passage
          </button>
        )}
        {(card.tags ?? []).slice(0, 4).map((tag) => (
          <span key={tag} className="tag">
            {tag}
          </span>
        ))}

        <div className="card-actions">
          {card.status !== "accepted" && (
            <button className="btn-sm btn-good" onClick={() => setStatus("accepted")} disabled={busy}>
              Accept
            </button>
          )}
          {card.status !== "rejected" && (
            <button className="btn-sm btn-bad" onClick={() => setStatus("rejected")} disabled={busy}>
              Reject
            </button>
          )}
          {!editing && (
            <button className="btn-sm" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
          {card.type !== "image_occlusion" && (
            <button
              className="btn-sm"
              onClick={() => setRegenPrompt("")}
              disabled={regenerating}
              title="Rewrite this card from its source passage"
            >
              {regenerating ? <span className="spinner" /> : "Regenerate"}
            </button>
          )}
        </div>
      </div>

      {regenPrompt !== null && (
        <div className="row" style={{ marginTop: 10 }}>
          <input
            type="text"
            autoFocus
            placeholder="How should it change? (e.g. make it more specific, split into two)"
            value={regenPrompt}
            onChange={(e) => setRegenPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") regenerate(regenPrompt);
              if (e.key === "Escape") setRegenPrompt(null);
            }}
          />
          <button className="btn-sm btn-primary" onClick={() => regenerate(regenPrompt)}>
            Rewrite
          </button>
          <button className="btn-sm btn-ghost" onClick={() => setRegenPrompt(null)}>
            Cancel
          </button>
        </div>
      )}

      {showEvidence && (
        <div className="evidence">
          {card.evidence || "No source passage was captured for this card."}
        </div>
      )}
    </article>
  );
}

function OcclusionPreview({ url, shapes }: { url: string; shapes: OcclusionShape[] }) {
  return (
    <span className="occlusion-stage" style={{ maxWidth: 340 }}>
      <img src={url} alt="" />
      {shapes.map((shape, i) => (
        <span
          key={i}
          className="mask"
          style={{
            left: `${shape.left * 100}%`,
            top: `${shape.top * 100}%`,
            width: `${shape.width * 100}%`,
            height: `${shape.height * 100}%`,
            cursor: "default",
            borderRadius: shape.shape === "ellipse" ? "50%" : undefined,
          }}
        >
          {shape.ordinal}
        </span>
      ))}
    </span>
  );
}

function renderCloze(text: string): string {
  return text.replace(
    /\{\{c(\d+)::(.+?)(?:::(.*?))?\}\}/gs,
    (_m, num: string, answer: string) =>
      `<span class="cloze-gap" title="c${num}">${answer}</span>`,
  );
}
